import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { BannerSeverity } from '@nuance/ui';
import { api } from '../services/api';

interface BannerState {
  severity: BannerSeverity;
  message: string;
  active: boolean;
}

interface BannerContextValue {
  banner: BannerState;
  broadcast: (severity: BannerSeverity, message: string) => void;
  dismiss: () => void;
  loading: boolean;
}

const BannerContext = createContext<BannerContextValue | null>(null);

export function BannerProvider({ children }: { children: React.ReactNode }) {
  const [banner, setBanner] = useState<BannerState>({
    severity: 'operational',
    message: '',
    active: false,
  });
  const [loading, setLoading] = useState(true);

  // Fetch persisted banner on mount
  useEffect(() => {
    let cancelled = false;
    api.get<{ severity: string; message: string; active: boolean }>('/manage/platform-banner')
      .then((data) => {
        if (!cancelled) {
          setBanner({
            severity: (data.severity || 'operational') as BannerSeverity,
            message: data.message || '',
            active: !!data.active,
          });
        }
      })
      .catch(() => {
        // Banner fetch is non-critical — silent fail
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const broadcast = useCallback((severity: BannerSeverity, message: string) => {
    setBanner({ severity, message, active: true });
    // Persist to backend (fire-and-forget)
    api.put('/manage/platform-banner', { severity, message, active: true }).catch(() => {});
  }, []);

  const dismiss = useCallback(() => {
    setBanner((prev) => ({ ...prev, active: false }));
    // Persist dismissal (fire-and-forget)
    api.put('/manage/platform-banner', { severity: 'operational', message: '', active: false }).catch(() => {});
  }, []);

  return (
    <BannerContext.Provider value={{ banner, broadcast, dismiss, loading }}>
      {children}
    </BannerContext.Provider>
  );
}

export function useBanner() {
  const ctx = useContext(BannerContext);
  if (!ctx) throw new Error('useBanner must be used within BannerProvider');
  return ctx;
}
